import React, { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema, generateBreadcrumbSchema } from '../../utils/seo';

const NotaryServicesPage: React.FC = () => {
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'Notary Services Las Vegas NV - Professional Document Notarization | Speedy DMV',
    description: 'Certified Nevada notary public services in Las Vegas. Notarize vehicle titles, powers of attorney, real estate, and business documents. Combined with DMV services. Call 702.979.7202.',
    keywords: 'notary services Las Vegas NV, certified notary public Nevada, document notarization Las Vegas, notary DMV services Nevada',
    canonical: `${siteUrl}/notary-services-detail`,
    ogImage: `${siteUrl}/img/speedy-dmv-services.jpg`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        generateServiceSchema(
          'Professional Notary Public Services',
          'Certified Nevada notary public services in Las Vegas for vehicle titles, powers of attorney, real estate, and business documents.',
          '15'
        ),
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Services', url: '/services' },
          { name: 'Notary Services', url: '/notary-services-detail' }
        ])
      ]
    }
  });

  const notaryServices = [
    {
      icon: 'ri-file-text-line',
      title: 'Document Notarization',
      description: 'Notarize affidavits, contracts, powers of attorney, and other legal documents.'
    },
    {
      icon: 'ri-car-line',
      title: 'Vehicle Documents',
      description: 'Notarize vehicle sale documents, title transfers, and DMV-related paperwork.'
    },
    {
      icon: 'ri-home-line',
      title: 'Real Estate Documents',
      description: 'Notarize real estate contracts, deeds, and property-related legal documents.'
    },
    {
      icon: 'ri-briefcase-line',
      title: 'Business Documents',
      description: 'Notarize business contracts, corporate resolutions, and commercial agreements.'
    }
  ];

  const commonDocuments = [
    'Affidavits and sworn statements',
    'Powers of attorney',
    'Vehicle bill of sale',
    'Real estate contracts',
    'Loan documents',
    'Business agreements',
    'Medical directives',
    'Travel consent forms',
    'Financial documents',
    'Court documents'
  ];

  const requirements = [
    'Valid, current photo identification (driver license, passport, state ID)',
    'Original document to be notarized (no copies)',
    'All parties must be present during notarization',
    'Document must be complete (no blank fields)',
    'Signers must understand the document content',
    'Willingness to sign voluntarily (no coercion)'
  ];

  const processSteps = [
    {
      step: 1,
      title: 'Identity Verification',
      description: 'We verify the identity of all signers using acceptable photo identification.',
      time: '2 minutes'
    },
    {
      step: 2,
      title: 'Document Review',
      description: 'Review the document to ensure completeness and proper formatting.',
      time: '3 minutes'
    },
    {
      step: 3,
      title: 'Signing Process',
      description: 'Witness the signing of the document by all required parties.',
      time: '5 minutes'
    },
    {
      step: 4,
      title: 'Notarial Certificate',
      description: 'Complete the notarial certificate with official seal and signature.',
      time: '2 minutes'
    }
  ];

  const toggleExpanded = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-32">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-teal-900 via-teal-800 to-green-900 text-white py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Professional Notary Services
              </h1>
              <p className="text-xl mb-8 max-w-3xl mx-auto">
                Certified Nevada notary services for all your document notarization needs. Fast, reliable, 
                and professional notary services available alongside our DMV services.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="tel:702-979-7202" 
                   className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                  Call Now: (702) 979-7202
                </a>
                <a href="/appointment" 
                   className="bg-white text-teal-900 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                  Schedule Appointment
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Complete Notary Solutions
              </h2>
              <p className="text-lg text-gray-600">
                Professional notary services for individuals and businesses in Las Vegas
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {notaryServices.map((service, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-md text-center">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${service.icon} text-2xl text-teal-600`}></i>
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{service.title}</h3>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Documents We Notarize */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Documents We Notarize
                </h2>
                <p className="text-gray-600 mb-8">
                  We provide notary services for a wide range of documents, ensuring legal compliance 
                  and proper authentication for your important paperwork.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {commonDocuments.map((doc, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-5 h-5 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-xs text-teal-600"></i>
                      </div>
                      <span className="text-gray-800 text-sm">{doc}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Why Choose Our Notary Services?
                </h3>
                <div className="space-y-4">
                  {[
                    {
                      icon: 'ri-shield-check-line',
                      title: 'Nevada Certified',
                      description: 'Licensed and bonded Nevada notary public with current certification.'
                    },
                    {
                      icon: 'ri-time-line',
                      title: 'Same-Day Service',
                      description: 'Fast notarization service available with appointment or walk-in.'
                    },
                    {
                      icon: 'ri-map-pin-line',
                      title: 'Convenient Location',
                      description: 'Combine notary services with your DMV needs in one trip.'
                    },
                    {
                      icon: 'ri-customer-service-line',
                      title: 'Professional Service',
                      description: 'Experienced staff ensures proper document handling and compliance.'
                    }
                  ].map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <i className={`${feature.icon} text-sm text-teal-600`}></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 text-sm">{feature.title}</h4>
                        <p className="text-gray-600 text-xs">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Requirements */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Notarization Requirements
              </h2>
              <p className="text-lg text-gray-600">
                Please ensure you meet these requirements before your notary appointment
              </p>
            </div>

            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded-lg shadow-md p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-6">
                  What You Need to Bring
                </h3>
                <div className="space-y-4">
                  {requirements.map((req, index) => (
                    <div key={index} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                      <div className="w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-sm text-teal-600"></i>
                      </div>
                      <span className="text-gray-800">{req}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Process Steps */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Notarization Process
              </h2>
              <p className="text-lg text-gray-600">
                Our professional notarization process ensures legal compliance and document integrity
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                {processSteps.map((item, index) => (
                  <div key={index} className="flex items-start space-x-6">
                    <div className="w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2">
                        <h3 className="text-xl font-semibold text-gray-900">{item.title}</h3>
                        <span className="text-sm font-medium text-teal-600 bg-teal-100 px-3 py-1 rounded-full">
                          {item.time}
                        </span>
                      </div>
                      <p className="text-gray-600">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Important Information */}
        <section className="py-16 bg-yellow-50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="bg-white rounded-lg shadow-md p-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-information-line text-2xl text-yellow-600"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    Important Notary Guidelines
                  </h3>
                  <div className="space-y-4 text-gray-700">
                    <div>
                      <h4 className="font-semibold mb-2">What We Cannot Do:</h4>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>Provide legal advice or explain document contents</li>
                        <li>Notarize incomplete documents with blank fields</li>
                        <li>Notarize documents for someone not physically present</li>
                        <li>Notarize documents without proper identification</li>
                        <li>Certify copies of vital records (birth certificates, etc.)</li>
                      </ul>
                    </div>
                    <div>
                      <h4 className="font-semibold mb-2">Acceptable Photo IDs:</h4>
                      <ul className="list-disc list-inside space-y-1 ml-4">
                        <li>Valid driver license or state ID card</li>
                        <li>Current passport</li>
                        <li>Military ID card</li>
                        <li>Other government-issued photo identification</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Notary Service Questions
              </h2>
            </div>

            <div className="space-y-4">
              {[
                {
                  question: "Do I need an appointment for notary services?",
                  answer: "While appointments are recommended for faster service, we also accept walk-ins. Call ahead to ensure availability and minimize wait time."
                },
                {
                  question: "How much do notary services cost?",
                  answer: "Nevada sets maximum fees for notary services. We'll explain all applicable fees when you schedule your appointment or arrive for service."
                },
                {
                  question: "Can you notarize documents in languages other than English?",
                  answer: "The document language doesn't matter, but all signers must be able to communicate with the notary in English or have a qualified translator present."
                },
                {
                  question: "What if I don't have acceptable identification?",
                  answer: "Nevada law requires specific forms of photo ID for notarization. We cannot proceed without acceptable identification from all signers."
                },
                {
                  question: "Can you explain what the document means?",
                  answer: "Notaries cannot provide legal advice or explain document contents. You should consult with an attorney if you need legal guidance about your documents."
                }
              ].map((faq, index) => (
                <div key={index} className="bg-white rounded-lg shadow-md">
                  <button
                    className="w-full px-6 py-4 text-left flex items-center justify-between focus:outline-none"
                    onClick={() => toggleExpanded(`faq-${index}`)}
                  >
                    <span className="font-semibold text-gray-900">{faq.question}</span>
                    <i className={`ri-arrow-${expandedSection === `faq-${index}` ? 'up' : 'down'}-s-line text-xl text-gray-500`}></i>
                  </button>
                  {expandedSection === `faq-${index}` && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-16 bg-teal-900 text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold mb-4">
              Need Notary Services?
            </h2>
            <p className="text-xl mb-8">
              📞 Appointments Required – Please Call Ahead
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:702-979-7202" 
                 className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                Call (702) 979-7202
              </a>
              <a href="/location" 
                 className="bg-white text-teal-900 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap">
                Visit Our Location
              </a>
            </div>
            <div className="mt-6 text-gray-300">
              <p>Speedy DMV Services - 4525 S Sandhill Rd, Las Vegas, NV</p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default NotaryServicesPage;
